import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
	Box,
	Paper,
	Typography,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	TextField,
	IconButton,
	Button,
	Alert,
	Switch,
	FormControlLabel,
	Breadcrumbs,
	Link,
	MenuItem,
	CircularProgress,
} from "@mui/material";
import {
	Edit as EditIcon,
	Plus as PlusIcon,
	Trash2 as TrashIcon,
	ChevronLeft as BackIcon,
	MessageSquare as QuizIcon, // Added quiz icon
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { getContentDisplayTitle } from "../../utils/contentDisplayTitles";
import GameContentQuizzes from "./GameContentQuizzes"; // We'll create this component next

const FIELDS_CONFIG = [
	{ name: "title", label: "Title", type: "text", required: true },
	{
		name: "content_type",
		label: "Content Type",
		type: "select",
		required: true,
		options: [
			{ value: "Text", label: "Text" },
			{ value: "Video", label: "Video" },
			{ value: "Audio", label: "Audio" },
		],
	},
	{ name: "content", label: "Content", type: "dynamic", required: true },
	{
		name: "introduction",
		label: "Introduction",
		type: "text",
		multiline: true,
	},
	{ name: "description", label: "Description", type: "text", multiline: true },
	{ name: "thumbnail", label: "Thumbnail", type: "file" },
	{ name: "status", label: "Status", type: "boolean" },
];

const ManageContents = () => {
	const { categoryId } = useParams();
	const navigate = useNavigate();
	const { supabase } = useAuth();
	const [category, setCategory] = useState(null);
	const [contents, setContents] = useState([]);
	const [openDialog, setOpenDialog] = useState(false);
	const [selectedItem, setSelectedItem] = useState(null);
	const [editForm, setEditForm] = useState({});
	const [successMessage, setSuccessMessage] = useState("");
	const [errorMessage, setErrorMessage] = useState("");
	const [thumbnailFile, setThumbnailFile] = useState(null);
	const [contentFile, setContentFile] = useState(null);
	const [formErrors, setFormErrors] = useState({});
	const [isSaving, setIsSaving] = useState(false);

	// New states for quiz management
	const [openQuizDialog, setOpenQuizDialog] = useState(false);
	const [selectedContent, setSelectedContent] = useState(null);

	const fetchCategory = async () => {
		try {
			const { data, error } = await supabase
				.from("course_categories")
				.select("*")
				.eq("id", categoryId)
				.single();

			if (error) throw error;
			setCategory(data);
		} catch (err) {
			console.error("Error fetching category:", err);
			setErrorMessage("Error loading category");
		}
	};

	const fetchContents = async () => {
		try {
			const { data, error } = await supabase
				.from("course_contents")
				.select(
					`
        id,
        title,
        content,
        content_type,
        introduction,
        description,
        thumbnail,
        status,
        category_id,
        created_at,
        updated_at
      `
				)
				.eq("category_id", categoryId)
				.order("title");

			if (error) throw error;

			// Ensure all items have a content_type
			const normalizedData = (data || []).map((item) => ({
				...item,
				content_type: item.content_type || "text",
			}));

			setContents(normalizedData);
		} catch (err) {
			console.error("Error fetching contents:", err);
			setErrorMessage("Error loading contents");
		}
	};

	useEffect(() => {
		if (categoryId) {
			fetchCategory();
			fetchContents();
		}
	}, [categoryId]);

	const validateForm = () => {
		const errors = {};

		FIELDS_CONFIG.forEach((field) => {
			// Skip content field validation here - we'll handle it separately
			if (field.name === "content") {
				return;
			}

			if (field.required && !editForm[field.name]) {
				errors[field.name] = `${field.label} is required`;
			}
		});

		// Handle content validation separately and more explicitly
		if (editForm.content_type !== "Text") {
			const isEditing = !!selectedItem;
			const hasExistingContent =
				editForm.content && editForm.content.trim().length > 0;
			const hasNewFile = !!contentFile;

			// Only require content if:
			// 1. It's a new item (not editing) AND no file uploaded
			// 2. It's editing AND no existing content AND no new file
			if (!isEditing && !hasNewFile) {
				errors.content = `Please upload ${editForm.content_type} content`;
			} else if (isEditing && !hasExistingContent && !hasNewFile) {
				errors.content = `Please upload ${editForm.content_type} content`;
			}
		} else {
			// For text content, check if content field has value
			if (!editForm.content || editForm.content.trim().length === 0) {
				errors.content = "Content is required";
			}
		}

		setFormErrors(errors);
		return Object.keys(errors).length === 0;
	};

	const handleAddNew = () => {
		setSelectedItem(null);
		setEditForm({
			category_id: categoryId,
			status: true,
			content_type: "Text", // Ensure this is always set
			content: "", // Initialize empty content
		});
		setThumbnailFile(null);
		setContentFile(null);
		setFormErrors({});
		setOpenDialog(true);
	};

	const handleEdit = (item) => {
		setSelectedItem(item);
		setEditForm({
			...item,
			content_type: item.content_type || "Text", // Provide default if missing
		});
		setThumbnailFile(null);
		setContentFile(null);
		setFormErrors({});
		setOpenDialog(true);
	};

	const handleDelete = async (id) => {
		if (!window.confirm("Are you sure you want to delete this content?"))
			return;

		try {
			const { error } = await supabase
				.from("course_contents")
				.delete()
				.eq("id", id);

			if (error) throw error;

			setSuccessMessage("Content deleted successfully");
			fetchContents();

			setTimeout(() => setSuccessMessage(""), 3000);
		} catch (err) {
			console.error("Error deleting content:", err);
			setErrorMessage("Error deleting content");
		}
	};

	// New function to handle opening the quiz dialog
	const handleManageQuizzes = (content) => {
		setSelectedContent(content);
		setOpenQuizDialog(true);
	};

	const handleFileChange = (event, type) => {
		const file = event.target.files[0];
		if (!file) return;

		if (type === "thumbnail") {
			if (file.size > 5 * 1024 * 1024) {
				// 5MB limit for thumbnails
				setErrorMessage("Thumbnail size should not exceed 5MB");
				return;
			}
			setThumbnailFile(file);
		} else {
			// Validate file type for content
			if (editForm.content_type === "Video") {
				// Check if file is MP4
				if (!file.type.includes("mp4") && !file.type.includes("video/mp4")) {
					setErrorMessage("Only MP4 video files are supported");
					return;
				}

				const maxSize = 100 * 1024 * 1024; // 100MB for video
				if (file.size > maxSize) {
					setErrorMessage(`Video size should not exceed 100MB`);
					return;
				}
			} else if (editForm.content_type === "Audio") {
				const maxSize = 20 * 1024 * 1024; // 20MB for audio
				if (file.size > maxSize) {
					setErrorMessage(`Audio size should not exceed 20MB`);
					return;
				}
			}

			setContentFile(file);
			// Clear any previous error messages
			setErrorMessage("");
		}
	};

	const uploadFile = async (file, type) => {
		const fileExt = file.name.split(".").pop();
		const fileName = `${Date.now()}-${Math.random()
			.toString(36)
			.substring(7)}.${fileExt}`;
		const filePath = `${type}/${fileName}`;

		// Use 'thumbnails' bucket for both thumbnails and content
		const { error: uploadError } = await supabase.storage
			.from("thumbnails")
			.upload(filePath, file);

		if (uploadError) throw uploadError;

		const {
			data: { publicUrl },
		} = supabase.storage.from("thumbnails").getPublicUrl(filePath);

		return publicUrl;
	};

	const handleSave = async () => {
		try {
			if (!validateForm()) {
				return;
			}

			// Set saving state to true to disable button and show loading indicator
			setIsSaving(true);

			const now = new Date().toISOString();
			const dataToSave = {
				...editForm,
				updated_at: now,
			};

			if (editForm.content_type !== "Text" && contentFile) {
				const contentUrl = await uploadFile(
					contentFile,
					editForm.content_type.toLowerCase()
				);
				dataToSave.content = contentUrl;
			}

			if (thumbnailFile) {
				const thumbnailUrl = await uploadFile(thumbnailFile, "thumbnail");
				dataToSave.thumbnail = thumbnailUrl;
			}

			if (!selectedItem) {
				dataToSave.created_at = now;
				const { error } = await supabase
					.from("course_contents")
					.insert([dataToSave]);

				if (error) throw error;
				setSuccessMessage("New content added successfully");
			} else {
				const { error } = await supabase
					.from("course_contents")
					.update(dataToSave)
					.eq("id", selectedItem.id);

				if (error) throw error;
				setSuccessMessage("Content updated successfully");
			}

			setOpenDialog(false);
			fetchContents();

			setTimeout(() => setSuccessMessage(""), 3000);
		} catch (err) {
			console.error("Error saving content:", err);
			setErrorMessage("Error saving content");
		} finally {
			// Reset saving state regardless of success or failure
			setIsSaving(false);
		}
	};

	const handleBackClick = () => {
		navigate("/manage-games");
	};

	const renderField = (field) => {
		if (field.type === "boolean") {
			return (
				<FormControlLabel
					key={field.name}
					control={
						<Switch
							checked={editForm[field.name] || false}
							onChange={(e) =>
								setEditForm({
									...editForm,
									[field.name]: e.target.checked,
								})
							}
							disabled={isSaving}
						/>
					}
					label={field.label}
					sx={{ mt: 2, display: "block" }}
				/>
			);
		}

		if (field.type === "file") {
			return (
				<Box key={field.name} sx={{ mt: 2 }}>
					<input
						accept="image/*"
						type="file"
						onChange={(e) => handleFileChange(e, "thumbnail")}
						style={{ display: "none" }}
						id="thumbnail-file"
						disabled={isSaving}
					/>
					<label htmlFor="thumbnail-file">
						<Button variant="outlined" component="span" disabled={isSaving}>
							Upload Thumbnail
						</Button>
					</label>
					{(thumbnailFile || editForm.thumbnail) && (
						<Typography variant="caption" sx={{ ml: 2 }}>
							{thumbnailFile ? thumbnailFile.name : "Current thumbnail exists"}
						</Typography>
					)}
				</Box>
			);
		}

		if (field.type === "select") {
			return (
				<TextField
					key={field.name}
					select
					label={field.label}
					fullWidth
					value={editForm[field.name] || ""}
					onChange={(e) => {
						const newValue = e.target.value;
						setEditForm((prev) => ({
							...prev,
							[field.name]: newValue,
							content: "", // Clear content when changing type
						}));
						setContentFile(null);
					}}
					error={!!formErrors[field.name]}
					helperText={formErrors[field.name]}
					required={field.required}
					disabled={isSaving}
					sx={{ mt: 2 }}>
					{field.options.map((option) => (
						<MenuItem key={option.value} value={option.value}>
							{option.label}
						</MenuItem>
					))}
				</TextField>
			);
		}

		if (field.type === "dynamic") {
			if (editForm.content_type === "Text") {
				return (
					<TextField
						key={field.name}
						label={field.label}
						fullWidth
						multiline
						rows={6}
						value={editForm[field.name] || ""}
						onChange={(e) =>
							setEditForm({
								...editForm,
								[field.name]: e.target.value,
							})
						}
						error={!!formErrors[field.name]}
						helperText={formErrors[field.name]}
						required={field.required}
						disabled={isSaving}
						sx={{ mt: 2 }}
					/>
				);
			} else {
				// Safe content type display
				const contentTypeDisplay = editForm.content_type
					? editForm.content_type.charAt(0).toUpperCase() +
					  editForm.content_type.slice(1)
					: "File";

				// Set accept attribute based on content type
				const acceptAttribute =
					editForm.content_type === "Video"
						? "video/mp4" // Specifically accept MP4 files for video
						: "audio/*"; // Accept all audio formats for audio

				return (
					<Box key={field.name} sx={{ mt: 2 }}>
						<input
							accept={acceptAttribute}
							type="file"
							onChange={(e) => handleFileChange(e, "content")}
							style={{ display: "none" }}
							id="content-file"
							disabled={isSaving}
						/>
						<label htmlFor="content-file">
							<Button variant="outlined" component="span" disabled={isSaving}>
								Upload {contentTypeDisplay}
							</Button>
						</label>
						{(contentFile || editForm.content) && (
							<Typography variant="caption" sx={{ ml: 2 }}>
								{contentFile ? contentFile.name : "Current content exists"}
							</Typography>
						)}
						{formErrors[field.name] && (
							<Typography
								color="error"
								variant="caption"
								sx={{ display: "block", mt: 1 }}>
								{formErrors[field.name]}
							</Typography>
						)}
						{editForm.content_type === "Video" && (
							<Typography
								variant="caption"
								color="text.secondary"
								sx={{ display: "block", mt: 1 }}>
								Only MP4 files are supported. Maximum size: 100MB
							</Typography>
						)}
						{editForm.content_type === "Audio" && (
							<Typography
								variant="caption"
								color="text.secondary"
								sx={{ display: "block", mt: 1 }}>
								Maximum size: 20MB
							</Typography>
						)}
					</Box>
				);
			}
		}

		return (
			<TextField
				key={field.name}
				label={field.label}
				type={field.type}
				fullWidth
				multiline={field.multiline}
				rows={field.multiline ? 4 : 1}
				value={editForm[field.name] || ""}
				onChange={(e) =>
					setEditForm({
						...editForm,
						[field.name]: e.target.value,
					})
				}
				error={!!formErrors[field.name]}
				helperText={formErrors[field.name]}
				required={field.required}
				disabled={isSaving}
				sx={{ mt: 2 }}
			/>
		);
	};

	return (
		<Box sx={{ p: 3 }}>
			{successMessage && (
				<Alert severity="success" sx={{ mb: 2 }}>
					{successMessage}
				</Alert>
			)}
			{errorMessage && (
				<Alert severity="error" sx={{ mb: 2 }}>
					{errorMessage}
				</Alert>
			)}

			<Breadcrumbs sx={{ mb: 3 }}>
				<Link
					component="button"
					variant="body1"
					onClick={handleBackClick}
					sx={{ display: "flex", alignItems: "center" }}>
					<BackIcon size={16} style={{ marginRight: "4px" }} />
					Game Categories
				</Link>
				<Typography color="text.primary">
					{category?.name || "Loading..."}
				</Typography>
			</Breadcrumbs>

			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					mb: 3,
				}}>
				<Typography variant="h5">Game Contents</Typography>
				<Button
					variant="contained"
					startIcon={<PlusIcon size={20} />}
					onClick={handleAddNew}>
					Add Content
				</Button>
			</Box>

			<Paper
				elevation={0}
				sx={{
					p: 3,
					border: "1px solid",
					borderColor: "divider",
					borderRadius: 2,
				}}>
				<TableContainer>
					<Table>
						<TableHead>
							<TableRow>
								<TableCell>Title</TableCell>
								<TableCell>Content Type</TableCell>
								<TableCell>Status</TableCell>
								<TableCell align="right">Actions</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{contents.map((content) => (
								<TableRow key={content.id}>
									<TableCell>{getContentDisplayTitle(content.title)}</TableCell>
									<TableCell>
										{content.content_type
											? content.content_type.charAt(0).toUpperCase() +
											  content.content_type.slice(1)
											: "-"}
									</TableCell>
									<TableCell>
										<Switch checked={content.status} size="small" disabled />
									</TableCell>
									<TableCell align="right">
										<IconButton
											size="small"
											color="secondary"
											onClick={() => handleManageQuizzes(content)}
											sx={{ mr: 1 }}
											title="Manage Quizzes">
											<QuizIcon size={16} />
										</IconButton>
										<IconButton
											size="small"
											onClick={() => handleEdit(content)}
											sx={{ mr: 1 }}>
											<EditIcon size={16} />
										</IconButton>
										<IconButton
											size="small"
											color="error"
											onClick={() => handleDelete(content.id)}>
											<TrashIcon size={16} />
										</IconButton>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</TableContainer>

				{contents.length === 0 && (
					<Box sx={{ py: 4, textAlign: "center" }}>
						<Typography color="text.secondary">No contents found</Typography>
					</Box>
				)}
			</Paper>

			{/* Content Edit/Add Dialog */}
			<Dialog
				open={openDialog}
				onClose={() => setOpenDialog(false)}
				maxWidth="sm"
				fullWidth>
				<DialogTitle>
					{selectedItem ? "Edit Content" : "Add New Content"}
				</DialogTitle>
				<DialogContent>
					{FIELDS_CONFIG.map((field) => renderField(field))}
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setOpenDialog(false)} disabled={isSaving}>
						Cancel
					</Button>
					<Button
						onClick={handleSave}
						variant="contained"
						disabled={isSaving}
						startIcon={
							isSaving ? <CircularProgress size={16} color="inherit" /> : null
						}>
						{isSaving
							? selectedItem
								? "Updating..."
								: "Adding..."
							: selectedItem
							? "Update"
							: "Add"}
					</Button>
				</DialogActions>
			</Dialog>

			{/* Quiz Management Dialog */}
			{openQuizDialog && selectedContent && (
				<GameContentQuizzes
					open={openQuizDialog}
					onClose={() => setOpenQuizDialog(false)}
					content={selectedContent}
					supabase={supabase}
				/>
			)}
		</Box>
	);
};

export default ManageContents;
